/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getAccountId,
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/helpers';
import {error} from '@ion/core/src/logger/logger';
import {getShipments} from '@ion/core/src/services/Connect';
import {
  Shipment,
  ShipmentsFilters
} from '@ion/core/src/types/entities';
import {
  ServiceError,
  ShipmentsSearch,
  ShipmentsSearchParams
} from '@ion/core/src/types/services';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {PageState} from '@/types/pages';
import {AdminAction, PageInitializeAction} from '@/types/redux';
import {gridDefaultPageSize} from '../../../utils/helpers';

// name of reducer
export const REDUCER_NAME                    = 'shipments';

export const INITIALIZE_SHIPMENTS            = 'INITIALIZE_SHIPMENTS';
export const SET_GRID_COLUMNS                = 'SET_SHIPMENTS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS             = 'UPDATE_SHIPMENTS_GRID_COLUMNS';
export const GET_SHIPMENTS_REQUEST           = 'GET_SHIPMENTS_REQUEST';
export const GET_SHIPMENTS_RESPONSE          = 'GET_SHIPMENTS_RESPONSE';
export const GET_SHIPMENTS_ERROR             = 'GET_SHIPMENTS_ERROR';
export const SHIPMENTS_GRID_REFRESHED        = 'SHIPMENTS_GRID_REFRESHED';
export const SET_GRID_PAGE_SIZE              = 'SET_SHIPMENTS_GRID_PAGE_SIZE';
export const RESEND_SHIPMENT                 = 'RESEND_SHIPMENT';

// search queries
export const UPDATE_SHIPMENTS_SEARCH_QUERIES = 'UPDATE_SHIPMENTS_SEARCH_QUERIES';
export const RESET_SHIPMENTS_SEARCH_QUERIES  = 'RESET_SHIPMENTS_SEARCH_QUERIES';

/**
 * initialize Shipments
 */
export const initializeShipments = (): PageInitializeAction => {
  return {type: INITIALIZE_SHIPMENTS};
};

export const buildShipmentsSearch = ({
  accountId,
  sort,
  searchParams = {}
}: ShipmentsSearchParams): ShipmentsSearch => {
  return {
    search : {
      sort,
      searchTerms : {
        accountId,
        ...createModularSearchFilters(searchParams)
      }
    }
  };
};

/**
 * get Shipments Data
 */
export const getShipmentsData = ({
  sort,
  callback,
  pageId = 1,
  pageSize = 20
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_SHIPMENTS_REQUEST});
    const {
      shipments: {
        search
      }
    } = getState();
    try {
      const {shipments: items, totalCount} = await getShipments({
        pageId,
        pageSize,
        filterOn : buildShipmentsSearch({
          sort,
          accountId    : getAccountId(getState),
          searchParams : search
        })
      });
      dispatch({type: GET_SHIPMENTS_RESPONSE});
      callback?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callback?.({error: err, items: [], totalCount: 0});
      dispatch({type: GET_SHIPMENTS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Invoked when Grid Refreshed
 */
export const shipmentsGridRefreshed = (): PageInitializeAction => {
  return {type: SHIPMENTS_GRID_REFRESHED};
};

/**
 * Invoked whenever user change the row per page dropdown
 */
export const setShipmentsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

// Updates the modular search queries for Shipments
export const updateShipmentsSearchQueries = (searchParams: ShipmentsFilters) => ({
  type : UPDATE_SHIPMENTS_SEARCH_QUERIES, searchParams
});

// Resets the Shipments filters state
export const resetShipmentsSearchQueries = () => ({
  type : RESET_SHIPMENTS_SEARCH_QUERIES
});

/**
 * Invoked when the resend shipment Dialog is to be displayed or removed.
 */
export const resendShipment = (shipment: Shipment, show = true) => ({
  type : RESEND_SHIPMENT, shipment, show
});

interface ResendShipmentAction extends AdminAction {
  shipment?: Shipment;
  shipments?: Shipment[];
};

interface ResendShipmentState {
  isResending: boolean;
  selected: Shipment | Shipment[] | null;
}

/**
 * REDUCERS
 */
export interface ShipmentsState extends PageState {
  shipments: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    pageSize: number
  },
  search: ShipmentsFilters,
  resend: ResendShipmentState
};

const clearSearch = (): ShipmentsFilters => ({
  trackingNum      : null,
  shipperId        : null,
  salesOrderNum    : null,
  purchaseOrderNum : null,
  ctcRegion        : null,
  shipDate         : null,
  createdDate      : null
});

// defines the user preference key for the shipments page ...
export const PREFERENCE = 'mfg-shipments';

// defines the initial state for the reducer ...
export const initialState: ShipmentsState = {
  isInitialized : false,
  shipments     : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    pageSize            : gridDefaultPageSize
  },
  search : clearSearch(),
  resend : {
    isResending : false,
    selected    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_SHIPMENTS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, shipments}: ShipmentsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : shipments.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: ShipmentsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: ShipmentsState, {pageSize}) => {
    grid.pageSize = pageSize;
  },
  [UPDATE_SHIPMENTS_SEARCH_QUERIES] : ({search}: ShipmentsState, {searchParams}) => {
    search.trackingNum      = searchParams?.trackingNum || null;
    search.shipperId        = searchParams?.shipperId || null;
    search.salesOrderNum    = searchParams?.salesOrderNum || null;
    search.purchaseOrderNum = searchParams?.purchaseOrderNum || null;
    search.ctcRegion        = searchParams?.ctcRegion || null;
    search.shipDate         = searchParams?.shipDate || null;
    search.createdDate      = searchParams?.createdDate || null;
  },
  [RESET_SHIPMENTS_SEARCH_QUERIES] : draft => {
    draft.search = clearSearch();
  },
  [RESEND_SHIPMENT] : ({resend}: ShipmentsState, {shipment, show}: ResendShipmentAction) => {
    resend.selected    = show ? shipment : null;
    resend.isResending = show;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: ShipmentsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
